import React, { useEffect, useState } from 'react';
import { SettingsManager, DataLibrary } from '../world-building';
import { PlotThreads } from '../world-building/PlotThreads';
import { saveSatelliteWidth, type PaneId } from './paneLayout';
import { ResidentPanel } from './ResidentPanel';

/**
 * **独立窗口**里的内容：整窗就是那一页（没有侧栏、编辑区、图标栏）。
 *
 * 窗口是**普通带边框窗口**（`decorations` 默认开），所以窗口标题栏、最小化、拖动位置
 * 全由操作系统提供 —— 这里不需要自绘标题栏，也不需要处理拖动或吸附。
 * 所谓"不限制吸附显示"就是这个意思：位置随用户摆布。
 *
 * 它是**独立的 SPA 实例**，与主窗口不共享内存：数据各自从后端取，主窗口切换章节
 * 这里不会跟随。这正是独立窗口只给数据页/余白、**不给 AI 助手**的原因
 * （副窗口拿不到主窗口里正在跑的那个回合）。
 */
export function SoloPaneWindow({ pane }: { pane: PaneId }) {
  /**
   * 把自己的宽度写回宽度配置（用户约定："用户调整了页面宽度则记录下来"）。
   *
   * 吸附窗口是**独立的 SPA 实例**，但同源 → 与主窗口共享同一个 localStorage，
   * 所以这里量到 `innerWidth` 直接写回去，主窗口下次打开就按这个宽度摆
   * （主窗口那边每次打开都会把 `satelliteWidth` 发给 Rust）。
   * 防抖 400ms：拖窗口边缘时每帧都会 resize。
   */
  useEffect(() => {
    let timer: number | undefined;
    const report = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => saveSatelliteWidth(window.innerWidth), 400);
    };
    report();
    window.addEventListener('resize', report);
    return () => { window.clearTimeout(timer); window.removeEventListener('resize', report); };
  }, []);

  /**
   * 这一窗当前显示哪一页。
   *
   * 初值来自路由（`?solo=`），之后的切换全靠**主窗口注入的事件**
   * （`xts:satellite-pane`，见 windowhost.rs）—— **不导航**，所以 SPA 不会重载、
   * 不会回到启动画面（早先用"改 hash"就是这样把副窗口弄成"准备同步… 0/5"的）。
   */
  const [current, setCurrent] = useState<PaneId>(pane);
  useEffect(() => {
    const onSwitch = (event: Event) => {
      const next = (event as CustomEvent<string>).detail;
      if (next === 'settings' || next === 'data' || next === 'threads' || next === 'resident') {
        setCurrent(next);
        // 标题栏由 Rust 改；这里只负责内容
      }
    };
    window.addEventListener('xts:satellite-pane', onSwitch);
    return () => window.removeEventListener('xts:satellite-pane', onSwitch);
  }, []);

  const body = {
    settings: <SettingsManager />,
    data: <DataLibrary initialTab="factions" />,
    threads: <PlotThreads />,
    resident: <ResidentPanel />,
    ai: null,
  }[current];

  if (!body) return null;
  return <div className="solo-pane-window">{body}</div>;
}
